import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, FileText, AlertTriangle, Shield, Scale, User, Mail } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <FileText className="h-16 w-16 text-[#4A90E2] mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            Terms of <span className="text-[#4A90E2]">Service</span>
          </h1>
          <p className="text-xl text-gray-300 mb-4">
            The legal stuff, explained in pilot-friendly language.
          </p>
          <p className="text-sm text-gray-400">
            Last Updated: January 23, 2026
          </p>
        </div>
      </section>

      {/* Critical Disclaimers */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-500/10 border-2 border-amber-500/30 rounded-xl p-8">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="h-8 w-8 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-heading font-bold text-white mb-4">⚠️ Read This First (Really Important)</h2>
                <div className="space-y-3 text-gray-300">
                  <p>
                    <strong className="text-white">FAR 117 Predictions Are Advisory:</strong> Winglet's FAR 117 compliance checks are predictions based on scheduled data, NOT official determinations. <span className="text-amber-400">You are responsible for your own rest and duty compliance.</span> Don't rely solely on Winglet to decide if you're legal to fly.
                  </p>
                  <p>
                    <strong className="text-white">Pay Calculations Are Reference Tools:</strong> Our pay calculations are based on our interpretation of PWA Section 12 rules. <span className="text-amber-400">They are not guaranteed to match your company's calculations.</span> Use Winglet to spot errors, but verify with your company payroll or union rep before filing a grievance.
                  </p>
                  <p>
                    <strong className="text-white">Not Liable for Flight Operations:</strong> Winglet is an audit and analysis tool. <span className="text-amber-400">We are not responsible for missed flights, fatigue, injuries, or any aviation incident</span> related to app usage, downtime, or calculation errors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#0F1824] border border-[#4A90E2]/30 rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold text-white mb-6">Table of Contents</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <TOCLink href="#acceptance" text="1. Acceptance of Terms" />
              <TOCLink href="#service" text="2. Description of Service" />
              <TOCLink href="#disclaimers" text="3. Disclaimers & Limitations" />
              <TOCLink href="#responsibilities" text="4. Your Responsibilities" />
              <TOCLink href="#ip" text="5. Intellectual Property" />
              <TOCLink href="#payment" text="6. Payment Terms" />
              <TOCLink href="#cancellation" text="7. Cancellation & Refunds" />
              <TOCLink href="#arbitration" text="8. Dispute Resolution" />
              <TOCLink href="#law" text="9. Governing Law" />
              <TOCLink href="#changes-terms" text="10. Changes to Terms" />
              <TOCLink href="#contact-terms" text="11. Contact" />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Section 1 */}
          <TermSection 
            id="acceptance"
            title="1. Acceptance of Terms"
            content={
              <>
                <p className="mb-4">
                  By creating an account, uploading rotations, or using any feature of Winglet, you agree to these Terms of Service and our <Link to="/privacy" className="text-[#4A90E2] hover:text-white">Privacy Policy</Link>.
                </p>
                <p className="text-gray-300">
                  If you don't agree with any part of these terms, please don't use Winglet. We're building a tool to help pilots get paid correctly—but we need clear legal boundaries to operate safely.
                </p>
              </>
            }
          />

          {/* Section 2 */}
          <TermSection 
            id="service"
            title="2. Description of Service"
            content={
              <>
                <p className="mb-4">
                  Winglet provides software tools for commercial airline pilots, including:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start space-x-2">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Pay Calculations:</strong> Parse rotations and timecards, calculate credit hours using PWA Section 12 rules, detect variances</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">FAR 117 Compliance:</strong> Check duty periods, rest requirements, rolling limits, and predict violations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Reroute Analysis:</strong> Classify reroutes (GREEN/YELLOW/RED), calculate Section 23L premiums</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Flight Tracking:</strong> Real-time OOOI times, gate changes, delay alerts</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Variance Detection:</strong> Compare your calculations vs company timecard, flag discrepancies</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Grievance Support:</strong> Generate audit trails with PWA citations for union reps</span>
                  </li>
                </ul>
                <p className="mt-4 text-gray-300">
                  Winglet is a <strong className="text-white">reference tool</strong>, not an official payroll system or legal compliance system.
                </p>
              </>
            }
          />

          {/* Section 3 */}
          <TermSection 
            id="disclaimers"
            title="3. Disclaimers & Limitations of Liability"
            content={
              <>
                <p className="text-white font-semibold mb-4">
                  Please read this section carefully—it limits our legal liability.
                </p>
                
                <DisclaimerBox 
                  title="FAR 117 Predictions (Not Guarantees)"
                  content={
                    <>
                      <p className="mb-3">
                        Winglet's FAR 117 compliance checks are <strong>predictions</strong> based on:
                      </p>
                      <ul className="space-y-2 text-sm mb-3">
                        <li>• Scheduled flight times (which may change)</li>
                        <li>• Parsed rotation data (which may contain errors)</li>
                        <li>• Our interpretation of FAR 117 rules (which may differ from FAA or your airline)</li>
                      </ul>
                      <p className="text-amber-400 font-semibold">
                        You are solely responsible for ensuring your own FAR 117 compliance. Do not rely on Winglet to determine if you're legal to fly. Check with your airline's scheduling or crew resources if unsure.
                      </p>
                    </>
                  }
                />

                <DisclaimerBox 
                  title="Pay Calculations (Reference Only)"
                  content={
                    <>
                      <p className="mb-3">
                        Winglet's pay calculations are based on our interpretation of PWA Section 12 rules. They are <strong>reference tools</strong>, not official payroll calculations.
                      </p>
                      <ul className="space-y-2 text-sm mb-3">
                        <li>• Your company's payroll system is the official source of truth</li>
                        <li>• Winglet may not account for airline-specific LOAs, MOUs, or side agreements</li>
                        <li>• Pay rates, rig calculations, and premium pay logic may differ from your contract</li>
                      </ul>
                      <p className="text-amber-400 font-semibold">
                        Use Winglet to spot potential errors, but verify with your company payroll or union rep before filing a formal grievance.
                      </p>
                    </>
                  }
                />

                <DisclaimerBox 
                  title="No Liability for Flight Operations"
                  content={
                    <p>
                      Winglet is an <strong>audit and analysis tool</strong>, not a flight planning or fatigue management system. <span className="text-amber-400">We are not liable for:</span>
                      <ul className="space-y-2 text-sm mt-3">
                        <li>• Missed flights due to app errors, downtime, or incorrect predictions</li>
                        <li>• Fatigue, injuries, or incidents related to FAR 117 violations</li>
                        <li>• Lost pay due to incorrect calculations or missed grievance deadlines</li>
                        <li>• Disciplinary action resulting from app usage or reliance on Winglet data</li>
                      </ul>
                    </p>
                  }
                />

                <div className="mt-6 bg-[#0F1824] border border-red-500/30 rounded-lg p-4">
                  <p className="text-white font-semibold mb-2">Maximum Liability:</p>
                  <p className="text-gray-300 text-sm">
                    To the fullest extent permitted by law, Winglet's total liability for any claim is limited to the amount you paid us in the 12 months before the claim (typically $179.88 for annual subscribers, or $14.99 for monthly).
                  </p>
                </div>
              </>
            }
          />

          {/* Section 4 */}
          <TermSection 
            id="responsibilities"
            title="4. Your Responsibilities"
            content={
              <>
                <p className="mb-4">
                  As a Winglet user, you agree to:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Provide Accurate Data:</strong> Upload correct rotation and timecard files. Winglet's calculations are only as accurate as the data you provide.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Maintain Account Security:</strong> Keep your password secure. Don't share your account with other pilots.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Comply with Company Policies:</strong> Ensure using Winglet doesn't violate your airline's IT policies. We're a third-party tool—your employer may have restrictions.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Verify Before Acting:</strong> Don't file grievances or refuse flights based solely on Winglet data. Verify with your union rep or company scheduling first.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Report Errors:</strong> If you find a bug or incorrect calculation, let us know at <a href="mailto:support@wingletapp.com" className="text-[#4A90E2] hover:text-white">support@wingletapp.com</a> so we can fix it for everyone.</span>
                  </li>
                </ul>
              </>
            }
          />

          {/* Section 5 */}
          <TermSection 
            id="ip"
            title="5. Intellectual Property"
            content={
              <>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-white font-semibold mb-2">Winglet Owns:</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li>• All source code, algorithms, and calculation logic</li>
                      <li>• The Winglet name, logo, and branding</li>
                      <li>• Any features, tools, or content we create</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">You Own:</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li>• Your rotation and timecard data</li>
                      <li>• Any notes, comments, or custom entries you create</li>
                      <li>• The right to download or delete your data anytime</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 text-gray-300">
                  You grant Winglet a limited license to process your data to provide the service (calculate pay, check compliance, etc.). We don't claim ownership of your rotation data.
                </p>
              </>
            }
          />

          {/* Section 6 */}
          <TermSection 
            id="payment"
            title="6. Payment Terms"
            content={
              <>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Subscription Pricing:</strong> $14.99/month or $179.88/year (save 2 months)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Free Trial:</strong> 45 days (1.5 months) free—no credit card required. Cancel anytime before trial ends.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Billing:</strong> Subscriptions auto-renew monthly or annually. You'll receive an email 7 days before renewal.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Taxes:</strong> Prices exclude applicable sales tax, which will be added at checkout based on your location.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Payment Methods:</strong> We accept credit/debit cards via Stripe. No checks or PayPal at this time.</span>
                  </li>
                </ul>
              </>
            }
          />

          {/* Section 7 */}
          <TermSection 
            id="cancellation"
            title="7. Cancellation & Refunds"
            content={
              <>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
                  <p className="text-white font-semibold mb-2">One-Click Cancellation, Anytime</p>
                  <p className="text-gray-300 text-sm">
                    Cancel your subscription anytime from Settings → Subscription. No questions asked, no phone calls, no retention offers. Just click "Cancel" and you're done.
                  </p>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">When You Cancel:</strong> Your subscription continues until the end of your current billing period. After that, no further charges.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Refunds:</strong> Monthly subscriptions are non-refundable. Annual subscriptions: prorated refund if canceled within first 30 days.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Data After Cancellation:</strong> Your account remains accessible (read-only) for 60 days. Download your data before then—we delete accounts 60 days after cancellation.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Reactivation:</strong> Resubscribe anytime to restore full access. Your data is retained for 60 days.</span>
                  </li>
                </ul>
              </>
            }
          />

          {/* Section 8 */}
          <TermSection 
            id="arbitration"
            title="8. Dispute Resolution & Arbitration"
            content={
              <>
                <p className="mb-4">
                  If you have a dispute with Winglet, we encourage you to contact us first at <a href="mailto:legal@wingletapp.com" className="text-[#4A90E2] hover:text-white">legal@wingletapp.com</a>. We'll work to resolve it informally.
                </p>
                <div className="bg-[#0F1824] border border-[#4A90E2]/30 rounded-lg p-6">
                  <h4 className="text-white font-semibold mb-3">Binding Arbitration</h4>
                  <p className="text-gray-300 text-sm mb-3">
                    If we can't resolve the dispute informally, you agree to <strong>binding arbitration</strong> instead of court litigation. Arbitration is faster, cheaper, and less formal than a lawsuit.
                  </p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Arbitration will be conducted by the American Arbitration Association (AAA) under its Consumer Arbitration Rules</li>
                    <li>• You may choose arbitration location (your home city or remote/virtual)</li>
                    <li>• Winglet pays all AAA fees for claims under $10,000</li>
                    <li>• Each party pays their own attorney fees unless arbitrator awards otherwise</li>
                  </ul>
                </div>
                <div className="mt-4 bg-[#0F1824] border border-red-500/30 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">No Class Actions</h4>
                  <p className="text-gray-300 text-sm">
                    You agree to resolve disputes on an <strong>individual basis only</strong>—no class actions, class arbitrations, or representative actions. Each pilot's claim must be arbitrated separately.
                  </p>
                </div>
                <p className="mt-4 text-gray-300 text-sm">
                  You may opt out of arbitration within 30 days of accepting these Terms by emailing <a href="mailto:legal@wingletapp.com" className="text-[#4A90E2] hover:text-white">legal@wingletapp.com</a> with "Arbitration Opt-Out" in the subject line.
                </p>
              </>
            }
          />

          {/* Section 9 */}
          <TermSection 
            id="law"
            title="9. Governing Law"
            content={
              <p className="text-gray-300">
                These Terms are governed by the laws of the State of <strong className="text-white">[State TBD]</strong>, without regard to conflict of law principles. Any disputes not subject to arbitration will be resolved in the courts of <strong className="text-white">[State TBD]</strong>.
              </p>
            }
          />

          {/* Section 10 */}
          <TermSection 
            id="changes-terms"
            title="10. Changes to These Terms"
            content={
              <div>
                <p className="text-gray-300 mb-4">
                  We may update these Terms occasionally to reflect new features, legal requirements, or operational changes. If we make significant changes, we'll:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span>Email you 30 days before changes take effect</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span>Display a notification in the app</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span>Update the "Last Updated" date at the top of this page</span>
                  </li>
                </ul>
                <p className="text-gray-300 mt-4">
                  Continued use of Winglet after changes take effect means you accept the new Terms. If you don't agree, cancel your subscription before the effective date.
                </p>
              </div>
            }
          />

          {/* Section 11 */}
          <TermSection 
            id="contact-terms"
            title="11. Contact Us"
            content={
              <div>
                <p className="text-gray-300 mb-4">
                  Questions about these Terms? Legal concerns? Contact us:
                </p>
                <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <Mail className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold mb-1">Email:</p>
                      <a href="mailto:legal@wingletapp.com" className="text-[#4A90E2] hover:text-white text-lg">
                        legal@wingletapp.com
                      </a>
                      <p className="text-gray-400 text-sm mt-2">
                        We'll respond within 48 hours for legal inquiries.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto text-center">
          <Scale className="h-16 w-16 text-[#4A90E2] mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Fair Terms, Transparent Service
          </h2>
          <p className="text-gray-300 mb-8">
            We're pilots building for pilots. These terms exist to protect both of us—not to trick you with fine print.
          </p>
          <Link 
            to="/signup" 
            className="inline-block bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105"
          >
            Start Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const TOCLink = ({ href, text }) => (
  <a 
    href={href} 
    className="text-gray-300 hover:text-[#4A90E2] transition-colors flex items-center space-x-2"
  >
    <span className="w-2 h-2 bg-[#4A90E2]/50 rounded-full"></span>
    <span>{text}</span>
  </a>
);

const TermSection = ({ id, title, content }) => (
  <div id={id} className="scroll-mt-24">
    <h2 className="text-3xl font-heading font-bold text-white mb-6">{title}</h2>
    <div className="space-y-4">
      {content}
    </div>
  </div>
);

const DisclaimerBox = ({ title, content }) => (
  <div className="bg-[#0F1824] border border-amber-500/30 rounded-lg p-6 mb-6">
    <h4 className="text-white font-semibold mb-3 flex items-center space-x-2">
      <AlertTriangle className="h-5 w-5 text-amber-500" />
      <span>{title}</span>
    </h4>
    <div className="text-gray-300 text-sm">
      {content}
    </div>
  </div>
);

export default TermsOfService;
